import React, {useState, useEffect, useContext} from "react";
import {withAuth} from "../../hoc/Auth";
import Api from "../../Api";
import {Wrapper, Container, Column} from "../../hoc/Layout";

import {MultipleSelect} from "../../Components/Utils/FieldsHelper";
import ResponsiveSidebarFilters from "../../Components/Utils/ResponsiveSidebarFilters";

import PayoutsFilters from "../../Components/PayoutsFilters";
import DateRange from "../../Components/Utils/DateRange";
import RegistryList from "../../Components/RegistryList";

import EmptyCard from "../../hoc/EmptyCard";
import MessageContext from "../../MessageContext";
import ErrorHandler from "../../Services/ErrorHandler";
import ApiResponseValidator from "../../Services/ApiResponseValidator";
import MessageFormatter from "../../Services/MessageFormatter";

import Pagination from "react-js-pagination";

import classes from "./styles.module.css";

const ACCESS_SBP_REGISTRY = 8192;

function checkAccess(accountAccessSettings, globalPrivileges, bitMask) {
    if (globalPrivileges !== null && globalPrivileges !== undefined) {
        return (globalPrivileges & bitMask) > 0;
    }
    if (Array.isArray(accountAccessSettings)) {
        return accountAccessSettings.some(el => (el.privileges & bitMask) > 0);
    }
    return false;
}

export default withAuth(({token, accountAccessSettings, globalPrivileges}) => {

        const [projects, setProjects] = useState(null);
        const [terminals, setTerminals] = useState(null);

        const [currentRegistryPage, setCurrentRegistryPage] = useState(1);
        const [postsPerRegistryPage, setPostsPerRegistryPage] = useState(15);

        const [registryList, setRegistryList] = useState([]);

        const [tagsList, setTagsList] = useState([]);

        const [registryListCount, setRegistryListCount] = useState(0);

        const [filterProjects, setFilterProjects] = useState([]);
        const [filterTerminals, setFilterTerminals] = useState([]);
        const [filterDate, setFilterDate] = useState({});
        const [filterTagsList, setFilterTagsList] = useState([]);
        const [filters, setFilters] = useState([]);

        const [isLoadingProjects, setIsLoadingProjects] = useState(true);
        const [loadingRegistryList, setLoadingRegistryList] = useState(false);
        const [loadingTagsList, setLoadingTagsList] = useState(false);

        const [isOpenModal, setIsOpenModal] = useState(false);

        const {onMessage} = useContext(MessageContext);

        useEffect(() => {
            const loadInitialData = async () => {
                try {
                    // Load projects
                    try {
                        const projectsList = await new Api({token}).getProjects();
                        ApiResponseValidator.validateListResponse({data: projectsList});
                        setProjects(projectsList);
                    } catch (error) {
                        ErrorHandler.logError(error, { action: 'loadProjects' });
                        onMessage(MessageFormatter.error('Failed to load projects'));
                        setProjects([]);
                    }

                    // Load initial registry list
                    try {
                        const registryResponse = await new Api({token}).setMobileRegistryList({
                            itemsPerPage: postsPerRegistryPage,
                            page: currentRegistryPage
                        });
                        ApiResponseValidator.validatePaginationResponse(registryResponse);
                        setRegistryList(registryResponse.data || []);
                        setRegistryListCount(registryResponse.count || 0);
                    } catch (error) {
                        ErrorHandler.logError(error, { action: 'loadMobileRegistryList' });
                        onMessage(MessageFormatter.error('Failed to load registry list'));
                        setRegistryList([]);
                    }

                    // Load all tags
                    try {
                        const allTags = await new Api({token}).getAllTags();
                        setTagsList(Array.isArray(allTags) ? allTags : []);
                    } catch (error) {
                        ErrorHandler.logError(error, { action: 'loadAllTags' });
                        // Don't show error for tags - non-critical
                        setTagsList([]);
                    }

                } finally {
                    setIsLoadingProjects(false);
                    setLoadingRegistryList(true);
                    setLoadingTagsList(true);
                }
            };

            loadInitialData();
        }, [token, onMessage])

        useEffect(()=> {
            if (!projects) return;

            let terminals = [];
            let cleanFilterTerminals = [];

            filterProjects.forEach(projectUUID => {
                const result = projects.find(el => el.uuid === projectUUID);
                if (result && result.providers) {
                    result.providers.forEach(el => {
                        terminals.push(el);
                    })
                }
            });

            filterTerminals.forEach(uuid => {
                const result = terminals.find(el => el.uuid !== uuid);
                if(result !== undefined)
                    cleanFilterTerminals.push(result.uuid);
            })

            setFilterTerminals(cleanFilterTerminals);
            setTerminals(terminals);

        }, [filterProjects, projects])

        function onSelectProjects(data) {
            setFilterProjects(data);
        }

        function selectFilter(filter) {
            const key = Object.keys(filter)[0];
            const value = Object.values(filter)[0];

            if (value) {
                setFilters([...filters, filter]);
            } else {
                const newFilters = filters.filter(el => Object.keys(el)[0] !== key);
                setFilters(newFilters);
            }
        }

        async function applyFilters(registryPage) {
            try {
                setLoadingRegistryList(false);

                const page = registryPage || 1;
                setCurrentRegistryPage(page);

                const registryFilters = {
                    projects: filterProjects,
                    providers: filterTerminals,
                    tags: filterTagsList,
                    filters: filters,
                    dateRange: filterDate,
                    page: page,
                    itemsPerPage: postsPerRegistryPage
                }

                const response = await new Api({token}).setMobileRegistryList(registryFilters);
                ApiResponseValidator.validatePaginationResponse(response);
                
                setRegistryList(response.data || []);
                setRegistryListCount(response.count || 0);

            } catch (error) {
                ErrorHandler.logError(error, { 
                    action: 'applyFilters',
                    filterProjects,
                    page: registryPage
                });
                onMessage(MessageFormatter.error('Failed to apply filters'));
                setRegistryList([]);
            } finally {
                setLoadingRegistryList(true);
            }
        }

        async function setRegistryPage(page, itemsPerPage) {
            try {
                const nextPage = page || 1;
                const nextItemsPerPage = itemsPerPage || postsPerRegistryPage;

                setCurrentRegistryPage(nextPage);
                setPostsPerRegistryPage(nextItemsPerPage);

                const registryFilters = {
                    projects: filterProjects,
                    providers: filterTerminals,
                    filters: filters,
                    dateRange: filterDate,
                    page: nextPage,
                    itemsPerPage: nextItemsPerPage
                }

                const response = await new Api({token}).setMobileRegistryList(registryFilters);
                ApiResponseValidator.validatePaginationResponse(response);
                
                setRegistryList(response.data || []);
                setRegistryListCount(response.count || 0);

            } catch (error) {
                ErrorHandler.logError(error, { 
                    action: 'setRegistryPage',
                    page
                });
                onMessage(MessageFormatter.error('Failed to load page'));
            }
        }

        function onSelectedTag(tags) {
            let filterTags = [];

            tags.forEach(tag => {
                if (!filterTags.includes(tag.id))
                    filterTags.push(tag.id);
            })

            setFilterTagsList(filterTags);
        }

        function onSelectTerminals(data) {
            setFilterTerminals(data);
        }

        if (!checkAccess(accountAccessSettings, globalPrivileges, ACCESS_SBP_REGISTRY)) {
            return (
                <Wrapper>
                    <Container style={{justifyContent: "center"}}>
                        <Column width={1180}>
                            <div className="d-flex justify-content-center align-items-center" style={{minHeight: '60vh'}}>
                                <div className="text-center">
                                    <h4>Нет доступа</h4>
                                    <p className="text-muted">У вас нет прав для просмотра этого раздела</p>
                                </div>
                            </div>
                        </Column>
                    </Container>
                </Wrapper>
            );
        }

        return (
            <Wrapper>
                <Container>
                    <Column width="780" className={classes.MainColumn}>
                        <button type="button" className={classes.FiltersToggle} onClick={() => setIsOpenModal(true)}>
                            Фильтры
                        </button>

                        <div className={classes.Card}>
                            <RegistryList token={token} data={registryList} isLoading={loadingRegistryList}/>

                            {postsPerRegistryPage < registryListCount &&
                                <div className={classes.PaginationWrap}>
                                    <Pagination
                                        firstPageText="⇤"
                                        prevPageText="⇠"
                                        lastPageText="⇥"
                                        nextPageText="⇢"
                                        activeLinkClass={classes.ActiveLink}
                                        activePage={currentRegistryPage}
                                        itemsCountPerPage={postsPerRegistryPage}
                                        totalItemsCount={registryListCount}
                                        pageRangeDisplayed={5}
                                        onChange={(page) => setRegistryPage(page, postsPerRegistryPage)}
                                    />
                                </div>
                            }
                        </div>
                    </Column>

                    <Column width="350" className={classes.SidebarColumn}>
                        <ResponsiveSidebarFilters title="Фильтры" isOpen={isOpenModal} onClose={() => setIsOpenModal(false)}>
                            <EmptyCard>
                                <MultipleSelect
                                    label={"FieldsHelper.label.project"}
                                    placeholder={"FieldsHelper.label.selectProject"}
                                    values={projects}
                                    selectedValues={filterProjects}
                                    isLoading={isLoadingProjects}
                                    onChange={onSelectProjects}
                                    excludeSearchColumn={[]}
                                />
                                <MultipleSelect
                                    label={"FieldsHelper.label.terminal"}
                                    placeholder={"FieldsHelper.label.selectTerminal"}
                                    values={terminals}
                                    selectedValues={filterTerminals}
                                    isLoading={isLoadingProjects}
                                    onChange={onSelectTerminals}
                                    excludeSearchColumn={[]}
                                />
                            </EmptyCard>

                            <EmptyCard>
                                <DateRange onSelectDate={(range) => setFilterDate(range)} defaultPeriod={'halfYear'}/>
                            </EmptyCard>

                            <PayoutsFilters loadingTagsList={loadingTagsList}
                                            tagsList={tagsList}
                                            onSelectFilter={selectFilter}
                                            filterProjects={filterProjects}
                                            onApplyFilters={applyFilters}
                                            onSelectedTag={onSelectedTag}
                                            isLoading={loadingRegistryList}
                            />
                        </ResponsiveSidebarFilters>
                    </Column>
                </Container>
            </Wrapper>
        )

    }
)



// WEBPACK FOOTER //
// ./src/PagesOfUser/MobilePayouts/index.js